import React, { useState, useCallback } from 'react';
import { ServiceRequestAPI } from '../../../api';
import { ServiceRequestStore } from '../../../store';
import * as QueryString from 'query-string';
import * as gtm from '../../../lib/gtm';
import { requestSteps } from './RequestSteps';
import * as track from '../../../lib/track';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

const delayMs = 1200;

export const withRequestFlowController = BaseComponent => ({ ...props }) => {
  const history = useHistory();
  const location = useLocation();
  const { variant, slug, providerUuid, providerName, listingUuid, listingTitle, service } = props;
  const steps = requestSteps[variant];
  const [requestId, setRequestId] = useState(null);
  const [valid, setValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const urlParams = QueryString.parse(location.search);
  const step = ('step' in urlParams) ? parseInt(urlParams.step) : 1;
  const fromStep = ('fromStep' in urlParams) ? parseInt(urlParams.fromStep) : 0;
  const progress = ((step-1)/(steps.length-1)) * 100;
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const profile = useSelector(state => state.auth.profile);
  const data = useSelector(state => state.serviceRequest);
  const dispatch = useDispatch();

  const validate = () => {
    if (!('email' in data.contact)) return false;
    if (!('phone' in data.contact)) return false;
    if (data.contact.email === '') return false;
    if (data.contact.phone === '') return false;
    if (data.services.length === 0) return false;
    if (data.request_type === '') return false;
    if (data.participant_type === '') return false;
    if (data.management_type === '') return false;
    if (data.age_group === '') return false;
    if (data.location === '') return false;
    return true;
  };

  const makeNextUrl = (variant, step, newStep, slug) => {
    if (variant === 'match') {
      return `/requestmatch?step=${newStep}&fromStep=${step}`;
    } else {
      return `/requestservice/${variant}/${slug}?step=${newStep}&fromStep=${step}`;
    }
  };

  const pushStep = (newStep) => {
    const nextUrl = makeNextUrl(variant, step, newStep, slug);
    history.push(nextUrl);
  };

  const nextStep = () => {
    const newStep = step === steps.length ? steps.length : step + 1;
    pushStep(newStep);
    window.scrollTo(0,0);
  };

  const previousStep = () => {
    let newStep = step === 0 ? 0 : step - 1;
    pushStep(newStep);
    window.scrollTo(0,0);
  };

  const handleAnswer = (item, value) => {
    dispatch(ServiceRequestStore.setItem(item, value));
    track.sendEvent(`request ${variant} question`, 'submit', {
      question: item,
      value: value,
      step: step,
      listing_uuid: listingUuid,
    }, [providerUuid]);

    // Service amounts question may be skipped if no services with amounts
    // applicable are selected. This step is different for each variant and
    // does not apply to listings. This handles the correct direction.
    if (variant === 'match' && step === 6 && fromStep > step) {
      previousStep();
    } else if (variant === 'provider' && step === 3 && fromStep > step) {
      previousStep();
    } else {
      nextStep();
    }

  };

  const submit = async () => {
    setBusy(true);
    try {
      const { payload } = await ServiceRequestAPI.createRequest(data);
      setRequestId(payload.event_group_id);
      const formName = variant === 'match' ? 'Brokerage Request Form' : 'Request Service';
      gtm.sendEvent('submit', {formName: formName});
      dispatch(ServiceRequestStore.reset()); // clear to prevent re-submissions
      nextStep();
    } finally {
      setBusy(false);
    }
  };

  const makeCompletedTo = () => {
    if (isAuthenticated) {
      return '/profile/requests';
    }
    if (variant === 'match') {
      return '/';
    }
    return `/${variant}/${slug}`;
  };

  const setProfileValues = useCallback(() => {
    if (isAuthenticated) {
      dispatch(ServiceRequestStore.setItem('account_uuid', profile.user_uuid));
      dispatch(ServiceRequestStore.setItem('location', profile.location === undefined ? '' : profile.location));
      dispatch(ServiceRequestStore.setItem('contact', {
        first_name: profile.first_name === undefined ? '' : profile.first_name,
        last_name: profile.last_name === undefined ? '' : profile.last_name,
        email: profile.email === undefined ? '' : profile.email,
        phone: profile.phone === undefined ? '' : profile.phone,
      }));
    }
  }, [dispatch, isAuthenticated, profile]);

  const setListingValues = useCallback(() => {
    if (providerUuid !== undefined) {
      dispatch(ServiceRequestStore.setItem('slug', slug));
      dispatch(ServiceRequestStore.setItem('provider_uuid', providerUuid));
      dispatch(ServiceRequestStore.setItem('provider_name', providerName));
    }
    if (listingUuid !== undefined) {
      dispatch(ServiceRequestStore.setItem('listing_uuid', listingUuid));
      dispatch(ServiceRequestStore.setItem('listing_title', listingTitle));
      dispatch(ServiceRequestStore.setItem('services', [{name: service}]));
    }
  }, [dispatch, slug, providerUuid, providerName, listingUuid, listingTitle, service]);

  React.useEffect(() => {
    setValid(validate());
  }, [step]);  // eslint-disable-line

  React.useEffect(() => {
    setProfileValues();
    setListingValues();
    track.sendEvent(`request ${variant} page`, 'view', {listing_uuid: listingUuid}, [providerUuid]);
  }, [setListingValues, setProfileValues, variant, listingUuid, providerUuid]);

  return (
    <BaseComponent
      requestId={requestId}
      completedTo={makeCompletedTo()}
      dispatch={dispatch}
      data={data}
      handleAnswer={handleAnswer}
      submit={submit}
      busy={busy}
      previousStep={previousStep}
      step={step}
      steps={steps}
      progress={progress}
      valid={valid}
      delayMs={delayMs}
      {...props}
    />
  );
};
